const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class SampleDetails extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this.ActiveSampleDetails = {};
		this.ActiveSampleLogs = [];
		this.SampleDetailsBackTo = null;
	}

	async onRenderAsync(pCallback)
	{
		this.log.info('Sample Details view rendered with Active Record', this.ActiveSampleDetails);

		if (pCallback) pCallback();
	}

	/**
	 * Open the Sample detail view for the selected Sample to view the full details
	 * @param {number|string} pIDSample - The ID of the Sample to view
	 * @param {object} pSampleRecord - The Sample record to view
	 * @param {string} pOpenTab - The tab to open when the Sample detail view is shown
	 * @return {Promise<void>}
	 * @async
	 */
	async showSampleDetailView(pIDSample, pSampleRecord, pOpenTab = 'Details')
	{
		// if a Sample record is passed in, use it, otherwise get the Sample record by ID
		if (pSampleRecord)
		{
			this.ActiveSampleDetails = Array.isArray(pSampleRecord) ? pSampleRecord[0] : pSampleRecord;
		}
		else
		{
			const tmpRecord = await this.pict.providers.SampleProvider.getSampleByID(pIDSample);
			this.ActiveSampleDetails = tmpRecord;
		}
		// if the Sample record is not found, log an error and return
		if (!this.ActiveSampleDetails)
		{
			this.log.error('Sample record not found for ID', pIDSample);
			return;
		}
		else
		{
			// get the logs for the Sample
			const tmpLogs = await this.pict.providers.SampleLogProvider.getSampleLogsByIDSample(this.ActiveSampleDetails.IDSample);
			this.ActiveSampleLogs = tmpLogs;
		}
		// render the Sample detail view
		this.renderAsync(() =>
		{
			this.log.info('Sample Details View Rendered with Active Record', this.ActiveSampleDetails);
			// open the panel if it is not already open
			this.pict.views.HeadlightPanels.handlePanelToggle('SampleDetails', 'open');
			// set the active tab to the one passed in
			this.pict.views.HeadlightPanels.toggleTabContent(`HeadlightPanels-SampleDetailsPanel-${pOpenTab}`, 'SampleDetails');
			if (pOpenTab === 'Print')
			{
				this.renderPrintPreview();
			}
		});
	}

	open(pIDSample, pSampleRecord, pOpenTab = 'Details')
	{
		this.resetSampleDetailsView();
		this.pict.views.HeadlightPanels.handlePanelToggle('Samples', 'open');
		this.showSampleDetailView(pIDSample, pSampleRecord, pOpenTab);
	}

	/**
	 * This method resets the state of the Sample Details view to a default state and removes
	 * any active classes on the Image Sets.
	 */
	resetSampleDetailsView()
	{
		this.log.info('Resetting Active Sample Details');
		this.ActiveSampleDetails = {};
	}

	/**
	 * This method returns the user to the Sample List view and resets the Sample Details view.
	 * This method is called when the user clicks the back button in the Sample Details view.
	 * @param {Event} event - The click event
	 * @return {void}
	 */
	returnToPreviousLocation(event)
	{
		// TODO: probably should update this to use data state to track pinned open panels
		if (event && event.target instanceof HTMLElement && event.target.closest('.hl-panel-wrapper').classList.contains('pinned-open'))
		{
			this.log.info('Panel is pinned open, not going back to Sample List view');
			return;
		}
		this.resetSampleDetailsView();
		// go back to the search view if the user came from there
		switch (this.SampleDetailsBackTo)
		{
		case 'Search':
			this.pict.views.HeadlightPanels.handlePanelToggle('Samples', 'open', () =>
			{
				this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SamplePanel-Search', 'Sample');
				this.pict.views.SampleSearch.updateWithSearchResults(this.pict.views.SampleSearch.CurrentSearchResults, this.pict.views.SampleSearch.CurrentSearchResults?.length);
			});
			break;
		case 'Scan':
			this.pict.views.HeadlightPanels.handlePanelToggle('Samples', 'open', () =>
			{
				this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SamplePanel-Scan', 'Sample');
			});
			break;
		case 'Current':
			this.pict.views.HeadlightPanels.handlePanelToggle('Samples', 'open', () =>
			{
				this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SamplePanel-Current', 'Sample');
				this.pict.views.SampleCurrent.handleTabOpened();
			});
			break;
		case 'Create':
			this.pict.views.HeadlightPanels.handlePanelToggle('Samples', 'open', () =>
			{
				this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SamplePanel-Create', 'Sample');
			});
			break;
		default:
			// close all panels and reset the Sample Details view (same as clicking the close button)
			this.pict.views.HeadlightPanels.closeAllUnpinnedPanels();
			this.resetSampleDetailsView();
			break;
		}
	}

	/**
	 * This method is used to add a new log to the Sample and refresh the Sample Details view.
	 * @return {Promise<void>}
	 * @async
	 */
	async addNewLogToSample()
	{
		if (!this.ActiveSampleDetails)
		{
			this.log.error('No active Sample to add log to');
			return;
		}
		this.pict.ContentAssignment.addClass('#HeadlightPanels-SampleDetailsPanel-Logs-Add-Button', 'is-loading');
		this.log.info('Adding new log to Sample ID:', this.ActiveSampleDetails.IDSample);

		const tmpLogMessage = this.pict.ContentAssignment.readContent('#SampleDetails-Tab-Logs-CreateNewLog-Textarea');
		await this.pict.providers.SampleLogProvider.addNote(this.ActiveSampleDetails.IDSample, tmpLogMessage);
		this.log.info('Sample Log added to Sample', this.ActiveSampleDetails.IDSample);
		await this.showSampleDetailView(this.ActiveSampleDetails.IDSample, null, 'Logs');
	}

	/**
	 * This method is used to clear the new log entry field in the Sample Details view.
	 * @return {void}
	 */
	clearNewLogEntry()
	{
		this.pict.ContentAssignment.assignContent('#SampleDetails-Tab-Logs-CreateNewLog-Textarea', '');
	}

	/**
	 * This method is used to handle the Print button click for building the print view.
	 * @param {boolean} [pShowOptions] - Open Print options (label stock and preview) instead of printing right away.
	 * @return {void}
	 */
	printQRCodeLabel(pShowOptions)
	{
		if (!this.ActiveSampleDetails)
		{
			this.log.error('No active Sample to print QR Code for');
			return;
		}
		this.log.info('Printing Sample Details');
		this.pict.providers.QRCodeProvider.printDecoratedEntity('Sample', this.ActiveSampleDetails.QRCode, undefined, undefined, this.ActiveSampleDetails, { ShowOptions: !!pShowOptions });
	}

	/**
	 * Render the QR label preview into the Print tab.
	 *
	 * `printDecoratedEntity` with a previewID short-circuits the print dialog and paints the label into
	 * that container instead -- the same call the real print uses, so what is previewed is what prints.
	 * @return {Promise<void>}
	 * @async
	 */
	async renderPrintPreview()
	{
		if (!this.ActiveSampleDetails)
		{
			return;
		}
		// per-customer label layout, loaded lazily the first time a label is needed
		await this.pict.providers.QRCodeProvider.ensurePrintableManifests();
		this.pict.providers.QRCodeProvider.printDecoratedEntity('Sample', this.ActiveSampleDetails.QRCode, 'SampleDetails-PrintPreview', undefined, this.ActiveSampleDetails);
	}

	/**
	 * This method is used to clear the QR Code label entry fields in the Sample Details view.
	 * @return {void}
	 */
	clearQRCodeLabel()
	{
		this.pict.ContentAssignment.assignContent('#SampleDetails-Tab-Print-HeaderText', '');
		this.pict.ContentAssignment.assignContent('#SampleDetails-Tab-Print-FooterText', '');
	}
}

module.exports = SampleDetails;

module.exports.default_configuration =
{
	ViewIdentifier: 'SampleDetails',
	DefaultRenderable: 'SampleDetails',
	DefaultDestinationAddress: '#HeadlightPanels-RightPanel',
	Templates:
	[
		{
			Hash: 'SampleDetails-Wrapper',
			Template: /*html*/`
			<article id="HeadlightPanels-SampleDetailsPanel" class="panel is-active">
				<!-- Sample ID: {~D:Context[0].ActiveSampleDetails.IDSample~} -->
				<div id="HeadlightPanels-SampleDetailsPanel-Sample-Header" class="panel-heading pr-4">
					<button type="button" class="is-pulled-right hl-panel-close-button" aria-label="close" title="Close panel" onclick="_Pict.views.HeadlightPanels.closeThisPanel(event);_Pict.views.SampleDetails.resetSampleDetailsView()">
						<span class="icon">
							<i class="fas fa-fw fa-times-circle" aria-hidden="true"></i>
						</span>
					</button>
					<button type="button" class="is-pulled-right hl-panel-pin-button mr-2" aria-label="pin" title="Pin content" onclick="_Pict.views.HeadlightPanels.togglePanelPinState()">
						<span class="icon">
							<i class="fas fa-fw fa-thumbtack" aria-hidden="true"></i>
						</span>
					</button>
					<span class="icon">
						<i id="HeadlightPanels-SampleDetailsPanel-Sample-Header-Icon" class="fas fa-fw fa-vial" aria-hidden="true"></i>
					</span>
					<span id="HeadlightPanels-SampleDetailsPanel-Sample-Header-Title" class="mr-3">Sample Details</span>
				</div>
				<!-- Sample Panel Tabs -->
				<div id="HeadlightPanels-SampleDetailsPanel-Tabs" class="panel-tabs pr-6">					
					<a id="HeadlightPanels-SampleDetailsPanel-Details-Tab" class="is-active" onclick="_Pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SampleDetailsPanel-Details', 'SampleDetails')">
						<span class="icon">
							<i class="fa fa-fw fa-info-circle"></i>
						</span>
						<span class="icon-text">
							Details
						</span>
					</a>
					<a id="HeadlightPanels-SampleDetailsPanel-Location-Tab" onclick="_Pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SampleDetailsPanel-Location', 'SampleDetails')">
						<span class="icon">
							<i class="fa fa-fw fa-location-pin"></i>
						</span>
						<span class="icon-text">
							Location
						</span>
					</a>
					<a id="HeadlightPanels-SampleDetailsPanel-Logs-Tab" onclick="_Pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SampleDetailsPanel-Logs', 'SampleDetails')">
						<span class="icon">
							<i class="fa fa-fw fa-list-timeline"></i>
						</span>
						<span class="icon-text">
							Logs
						</span>
					</a>
					<a id="HeadlightPanels-SampleDetailsPanel-Print-Tab" onclick="_Pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SampleDetailsPanel-Print', 'SampleDetails'); _Pict.views.SampleDetails.renderPrintPreview();">
						<span class="icon">
							<i class="fa fa-fw fa-qrcode"></i>
						</span>
						<span class="icon-text">
							Print
						</span>
					</a>
				</div>
				<!-- SampleDetails Panel Content -->
				<div id="HeadlightPanels-SampleDetailsPanel-Content" style="min-width:40vw" data-sample-id="{~D:Record.IDSample~}">
					{~T:SampleDetails-SampleHeader:Context[0].ActiveSampleDetails~}
					<!-- SampleDetails Content > Tab Details View -->
					<div id="HeadlightPanels-SampleDetailsPanel-Details-Content" class="panel-content panel-with-tabs-offset-plus-sample-header">
						<div id="SampleDetails-Details-Container">{~T:SampleDetails-Tab-Details:Context[0].ActiveSampleDetails~}</div>
					</div>
					<!-- SampleDetails Content > Tab Location View -->
					<div id="HeadlightPanels-SampleDetailsPanel-Location-Content" class="panel-content panel-with-tabs-offset-plus-sample-header">
						<div id="SampleDetails-Location-Container">{~T:SampleDetails-Tab-Location:Context[0].ActiveSampleDetails~}</div>
					</div>
					<!-- SampleDetails Content > Tab Logs View -->
					<div id="HeadlightPanels-SampleDetailsPanel-Logs-Content" class="panel-content panel-with-tabs-offset-plus-sample-header panel-has-nested-scroll-content">
						<div id="SampleDetails-Logs-Container">{~T:SampleDetails-Tab-Logs:Context[0].ActiveSampleLogs~}</div>
					</div>
					<!-- SampleDetails Content > Tab Print View -->
					<div id="HeadlightPanels-SampleDetailsPanel-Print-Content" class="panel-content panel-with-tabs-offset-plus-sample-header">
						<div id="SampleDetails-Print-Container">{~T:SampleDetails-Tab-Print:Context[0].ActiveSampleDetails~}</div>
					</div>
				</div>
			</article>`,
		},
		{
			Hash: 'SampleDetails-SampleHeader',
			Template: /*html*/`
			<div class="container columns has-background-text-15 is-vcentered mb-0" style="min-height: 130px">
				<div class="column px-5">
					<p class="title is-4 has-text-white">
						{~TIfAbs:SampleDetails-DatumValue-Name:Record:Record.Name^>^0~}
						{~TIfAbs:SampleDetails-DatumValue-Name-Empty:Record:Record.Name^<=^0~}
					</p>
					<p class="subtitle is-6 has-text-text-65 mb-2">
						<span class="icon">
							<i class="fas fa-fw fa-qrcode fa-sm"></i>
						</span>
						<span class="icon-text">{~D:Record.QRCode~}</span>
					</p>
					<span class="label is-size-7 has-text-text-65 mb-0">Status:</p>
					{~StatusPill:Record.Status~}
				</div>
				<div class="column is-3" style="flex-basis: content;">
					<figure class="image is-96x96 mr-1">{~QRCodeImage:Record.QRCode^96x96^~}</figure>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleDetails-Tab-Details',
			Template: /*html*/`
			<div class="columns px-3 m-0 mt-3">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Created by</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-User:Record:Record.CreatingIDUser^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.CreatingIDUser^<=^0~}
					</span>
				</div>
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Created on</span>
					<span class="subtitle is-6 has-text-text-90">
						{~DateFormat:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Project</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-Project:Record:Record.IDProject^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.IDProject^<=^0~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Line Item</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-MixDesign:Record:Record.IDLineItem^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.IDLineItem^<=^0~}
					</span>
				</div>
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Material Type</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-Material:Record:Record.IDMaterial^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.IDMaterial^<=^0~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Mix Design</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-MixDesign:Record:Record.IDMixDesign^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.IDMixDesign^<=^0~}
					</span>
				</div>
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Producer</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-Organization:Record:Record.IDOrganization^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.IDOrganization^<=^0~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Product</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-Product:Record:Record.IDProduct^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.IDProduct^<=^0~}
					</span>
				</div>
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Production Plant</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-EntityValue-ProductionPlant:Record:Record.IDProductionPlant^>^0~}
						{~TIfAbs:SampleDetails-EntityValue-NA:Record:Record.IDProductionPlant^<=^0~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Manufacturer Code</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-DatumValue-ManufacturerCode:Record:Record.ManufacturerCode^>^0~}
						{~TIfAbs:SampleDetails-DatumValue-NA:Record:Record.ManufacturerCode^<=^0~}
					</span>
				</div>
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Intended Use</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-DatumValue-IntendedUse:Record:Record.IntendedUse^>^0~}
						{~TIfAbs:SampleDetails-DatumValue-NA:Record:Record.IntendedUse^<=^0~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Represented Quantity</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-DatumValue-RepresentedQuantity:Record:Record.RepresentedQuantity^>^0~}
						{~TIfAbs:SampleDetails-DatumValue-NA:Record:Record.RepresentedQuantity^<=^0~}
					</span>
				</div>
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Units</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-DatumValue-RepresentedQuantityUnits:Record:Record.RepresentedQuantityUnits^>^0~}
						{~TIfAbs:SampleDetails-DatumValue-RepresentedQuantityUnits-Empty:Record:Record.RepresentedQuantityUnits^<=^0~}
					</span>
				</div>
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-60">Buy USA</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-DatumValue-BuyUSA-Yes:Record:Record.BuyUSA^>^0~}
						{~TIfAbs:SampleDetails-DatumValue-BuyUSA-No:Record:Record.BuyUSA^<=^0~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column p-3">
					<span class="title is-block is-6 has-text-text-65">Notes</span>
					<div class="subtitle is-6 has-text-text-90">
						{~T:SampleDetails-DatumValue-Notes:Record~}
					</div>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleDetails-EntityValue-NA',
			Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
		},
		{
			Hash: 'SampleDetails-DatumValue-NA',
			Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
		},
		{
			Hash: 'SampleDetails-DatumValue-Name',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-Name-Empty',
			Template: /*html*/`Unnamed Sample #{~D:Record.IDSample~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-Status',
			Template: /*html*/`{~D:Record.Status~}`,
		},
		{
			// Remarks are authored as markdown (the test forms' RichText editor writes markdown into
			// Notes), so they are rendered rather than printed raw. The expression carries its own
			// empty case, which also fixes remarks that start with `#` or `-`: the comparison this
			// replaced sorted those below '0' and showed N/A for a note that was actually there.
			Hash: 'SampleDetails-DatumValue-Notes',
			Template: /*html*/`{~MarkdownRemarks:Record.Notes^SampleDetails-DatumValue-NA^~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-GeographicalArea',
			Template: /*html*/`{~D:Record.GeographicalArea~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-ManufacturerCode',
			Template: /*html*/`{~D:Record.ManufacturerCode~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-IntendedUse',
			Template: /*html*/`{~D:Record.IntendedUse~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-BuyUSA-Yes',
			Template: /*html*/`Yes`,
		},
		{
			Hash: 'SampleDetails-DatumValue-BuyUSA-No',
			Template: /*html*/`No`,
		},
		{
			Hash: 'SampleDetails-DatumValue-ProductName',
			Template: /*html*/`{~D:Record.ProductName~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-RepresentedQuantity',
			Template: /*html*/`{~D:Record.RepresentedQuantity~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-RepresentedQuantityUnits',
			Template: /*html*/`{~D:Record.RepresentedQuantityUnits~}`,
		},
		{
			Hash: 'SampleDetails-DatumValue-RepresentedQuantityUnits-Empty',
			Template: /*html*/`Variable Units (English)`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Project',
			Template: /*html*/`{~Entity:Project^Record.IDProject^SampleDetails-EntityValue-Project-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Project-Record',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Material',
			Template: /*html*/`{~Entity:Material^Record.IDMaterial^SampleDetails-EntityValue-Material-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Material-Record',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-LineItem',
			Template: /*html*/`{~Entity:LineItem^Record.IDLineItem^SampleDetails-EntityValue-LineItem-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-LineItem-Record',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Product',
			Template: /*html*/`{~Entity:Product^Record.IDProduct^SampleDetails-EntityValue-Product-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Product-Record',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Organization',
			Template: /*html*/`{~Entity:Organization^Record.IDOrganization^SampleDetails-EntityValue-Organization-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-Organization-Record',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-ProductionPlant',
			Template: /*html*/`{~Entity:ProductionPlant^Record.IDProductionPlant^SampleDetails-EntityValue-ProductionPlant-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-ProductionPlant-Record',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-MixDesign',
			Template: /*html*/`{~Entity:MixDesign^Record.IDMixDesign^SampleDetails-EntityValue-MixDesign-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-MixDesign-Record',
			Template: /*html*/`{~D:Record.MixID~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-User',
			Template: /*html*/`{~Entity:User^Record.CreatingIDUser^SampleDetails-EntityValue-User-Record^~}`,
		},
		{
			Hash: 'SampleDetails-EntityValue-User-Record',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
		{
			Hash: 'SampleDetails-Tab-Location',
			Template: /*html*/`
			<figure class="image">
				<img class="has-background-text-40" onerror="this.onerror = null; this.src='./images/no-location-data-dark.png'" src="{~GoogleMapURL:Record^640x480^~}" alt="Sample location on a map" onload="_Pict.views.HeadlightPanels.removeLoadingSkeleton(this)">
			</figure>
			<div class="columns px-3 m-0 mt-3">
				<div class="column">
					<span class="title is-block is-6 has-text-text-60">Latitude</span>
					<span class="subtitle is-6 has-text-text-90">
						{~D:Record.CaptureLatitude~}
					</span>
				</div>
				<div class="column">
					<span class="title is-block is-6 has-text-text-60">Longitude</span>
					<span class="subtitle is-6 has-text-text-90">
						{~D:Record.CaptureLongitude~}
					</span>
				</div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column">
					<span class="title is-block is-6 has-text-text-60">Geographical Area</span>
					<span class="subtitle is-6 has-text-text-90">
						{~TIfAbs:SampleDetails-DatumValue-GeographicalArea:Record:Record.GeographicalArea^>^0~}
						{~TIfAbs:SampleDetails-DatumValue-NA:Record:Record.GeographicalArea^<=^0~}
					</span>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleDetails-Tab-Logs',
			Template: /*html*/`
				{~TIfAbs:SampleDetails-Tab-Logs-Items:Record:Record.length^>^0~}
				{~TIfAbs:SampleDetails-Tab-Logs-Empty:Record:Record.length^<=^0~}
				{~T:SampleDetails-Tab-Logs-Entry:Context[0].ActiveSampleDetails~}`,
		},
		{
			Hash: 'SampleDetails-Tab-Logs-Empty',
			Template: /*html*/`
			<div class="notification has-text-text-light is-dark m-3 is-fullwidth">
				<p class="has-text-centered">
					<span class="icon">
						<i class="fa fa-fw fa-info-circle"></i>
					</span>
					<span class="icon-text">No logs are available for this sample.</span>
				</p>
			</div>`,
		},
		{
			Hash: 'SampleDetails-Tab-Logs-Items',
			Template: /*html*/`
			<div id="SampleDetails-Tab-Logs-Items" class="nested-scroll-content panel-views-height-offset has-sample-header-offset">
				{~TS:SampleDetails-Tab-Logs-Item:Record~}
			</div>`,
		},
		{
			Hash: 'SampleDetails-Tab-Logs-Item',
			Template: /*html*/`
			<div class="panel-block is-block-flex is-align-items-left is-relative p-0">
				<figure class="image">
					<img class="preview-image" style="max-width: 100px;" onerror="this.onerror = null; this.src='./images/no-location-data-dark.png'" src="{~GoogleMapURL:Record^100x100^~}" alt="Geolocation for sample" />
				</figure>
				<div class="is-block-flex is-align-items-left mx-4 my-2" style="flex-grow: 1">
					<div class="is-size-7 has-text-text-75">#{~D:Record.IDSampleLog~}</div>
					<div>{~D:Record.Notes~}</div>
					<div class="is-size-7 has-text-text-75">
						<span class="has-text-text-60">on</span>
						<span class="has-text-text-80">{~DateFormat:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</span>
						<span class="has-text-text-60">by</span>
						<span class="has-text-text-80">{~Entity:User^Record.CreatingIDUser^SampleDetails-Tab-Logs-Item-User^~}</span>
					</div>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleDetails-Tab-Logs-Item-User',
			Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
		},
		{
			Hash: 'SampleDetails-Tab-Logs-Entry',
			Template: /*html*/`
			<div class="column" style="border-top: 1px solid var(--bulma-text-20)">
				<textarea id="SampleDetails-Tab-Logs-CreateNewLog-Textarea" style="min-height: 4rem" class="textarea" placeholder="Enter log notes here"></textarea>
				<div class="buttons mt-2 is-right">
					<button type="button" class="button is-primary is-soft" title="Clear log entry" onclick="_Pict.views.SampleDetails.clearNewLogEntry()">
						<span class="icon">
							<i class="fa fa-fw fa-refresh"></i>
						</span>
						<span>Clear</span>
					</button>
					<button type="button" class="button is-primary" title="Add new log to sample"onclick="_Pict.views.SampleDetails.addNewLogToSample()">
						<span class="icon">
							<i class="fa fa-fw fa-pencil"></i>
						</span>
						<span>Add Log</span>
					</button>
				</div>
			</div>`,
		},
		{
			Hash: 'SampleDetails-Tab-Print',
			Template: /*html*/`
			<div class="columns px-3 m-0">
				<div class="column has-background-white p-3" id="SampleDetails-PrintPreview"></div>
			</div>
			<div class="columns px-3 m-0">
				<div class="column">
					<div class="buttons mt-2 is-right">
						<button type="button" class="button is-primary" title="Print qrcode label" onclick="_Pict.views.SampleDetails.printQRCodeLabel()">
							<span class="icon">
								<i class="fa fa-fw fa-print"></i>
							</span>
							<span>Print QRCode Label</span>
						</button>
						<button type="button" class="button is-light" title="Choose the label stock and see the label before printing" onclick="_Pict.views.SampleDetails.printQRCodeLabel(true)">
							<span>Print options…</span>
						</button>
					</div>
				</div>
			</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'SampleDetails',
			TemplateHash: 'SampleDetails-Wrapper',
			DestinationAddress: '#HeadlightPanels-RightPanel',
			RenderType: 'replace',
		},
	],
};
